import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faBuilding,
  faTicketAlt,
  faShieldAlt,
  faCheck,
  faExclamationTriangle,
  faUpload,
  faSpinner,
  faSync,
  faLock,
  faFileInvoiceDollar,
} from "@fortawesome/free-solid-svg-icons";
import { Card, Form, Button, Row, Col, Alert, Spinner, Table, Badge } from "@themesberg/react-bootstrap";
import { client } from "../../api/client";

const cardStyle = { borderRadius: "8px" };
const inputStyle = {
  borderRadius: "4px",
  borderColor: "#CCC",
  height: "38px",
  fontSize: "0.9rem",
  color: "#111",
  fontWeight: 500,
};
const labelStyle = { color: "#333" };
const primaryBtnStyle = {
  backgroundColor: "#007BFF",
  borderColor: "#007BFF",
  borderRadius: "4px",
  fontSize: "0.95rem",
};

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user_profile") || "null");
  } catch (e) {
    return null;
  }
}

export function Profile() {
  const user = getStoredUser();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [logoUrl, setLogoUrl] = useState((user && user.company && user.company.logo) || "");
  const logoInputRef = useRef(null);

  const [profileInfo, setProfileInfo] = useState({
    owner: "",
    name: "",
    expiration: "",
    phone: "",
    email: "",
    rate_per_pos: "",
  });

  const [configInfo, setConfigInfo] = useState({
    header_name: "",
    address: "",
    header_phone: "",
    header_subtitle: "",
    footer_message: "",
  });

  const [ticketOptions, setTicketOptions] = useState({
    show_logo: true,
    show_footer: true,
    show_address: true,
    show_company_name: true,
    show_phone: true,
    show_free_mariage_price: true,
    show_branch: true,
    show_agent: true,
    max_cancel_minutes: 5,
  });

  const [passwordInfo, setPasswordInfo] = useState({
    new_password: "",
    confirm_password: "",
  });

  const [companyConfig, setCompanyConfig] = useState({});
  const [billingData, setBillingData] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  useEffect(() => {
    async function loadFullData() {
      try {
        setLoading(true);
        const stored = getStoredUser();
        const comp = await client("company/").catch(() => null);
        const companyData = comp || (stored && stored.company) || {};
        const cfg = companyData.config || {};
        setCompanyConfig(cfg);

        if (companyData.logo) setLogoUrl(companyData.logo);

        setProfileInfo({
          owner: (stored && (stored.first_name || stored.username)) || "",
          name: companyData.name || "",
          expiration: cfg.expiration_date || "",
          phone: (stored && stored.phone_number) || companyData.phone_number || "",
          email: (stored && stored.email) || "",
          rate_per_pos: companyData.billing_rate_per_agent
            ? `${companyData.billing_rate_per_agent} $ USD`
            : "",
        });

        setConfigInfo({
          header_name: cfg.header_title || companyData.name || "",
          address: companyData.headquarters_address || "",
          header_phone: companyData.phone_number || "",
          header_subtitle: cfg.header_subtitle || "",
          footer_message: companyData.ticket_footer_message || "",
        });

        setTicketOptions({
          show_logo: cfg.show_logo !== false,
          show_footer: cfg.show_footer !== false,
          show_address: cfg.show_address !== false,
          show_company_name: cfg.show_company_name !== false,
          show_phone: cfg.show_phone !== false,
          show_free_mariage_price: cfg.show_free_mariage_price !== false,
          show_branch: cfg.show_branch !== false,
          show_agent: cfg.show_agent !== false,
          max_cancel_minutes: cfg.max_cancel_minutes != null ? cfg.max_cancel_minutes : 5,
        });

        if (stored && (stored.role === "COMPANY_ADMIN" || stored.role === "SUPERADMIN")) {
          try {
            setBillingLoading(true);
            const billingRes = await client("billing/status/").catch(() => null);
            if (billingRes && billingRes.current_month) {
              setBillingData(billingRes.current_month);
            }
          } catch (bErr) {
            console.warn("Could not load billing:", bErr);
          } finally {
            setBillingLoading(false);
          }
        }
      } catch (e) {
        console.error("Error loading account details:", e);
      } finally {
        setLoading(false);
      }
    }
    loadFullData();
  }, []);

  const handleToggleOption = (key) => {
    setTicketOptions((prev) => {
      const next = { ...prev };
      next[key] = !prev[key];
      return next;
    });
  };

  const handleSaveAll = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setSuccess("");
    setError("");

    try {
      const companyPayload = {
        name: profileInfo.name,
        headquarters_address: configInfo.address,
        phone_number: configInfo.header_phone || profileInfo.phone,
        ticket_footer_message: configInfo.footer_message,
        config: Object.assign({}, companyConfig, {
          header_title: configInfo.header_name,
          header_subtitle: configInfo.header_subtitle,
          expiration_date: profileInfo.expiration,
        }, ticketOptions),
      };

      await client("company/", { method: "PUT", body: companyPayload });

      const userPayload = {
        first_name: profileInfo.owner,
        phone_number: profileInfo.phone,
        email: profileInfo.email,
      };

      if (passwordInfo.new_password) {
        if (passwordInfo.new_password !== passwordInfo.confirm_password) {
          throw new Error("Les mots de passe ne correspondent pas");
        }
        userPayload.password = passwordInfo.new_password;
      }

      await client("user/profile/", { method: "PUT", body: userPayload });

      setSuccess("Konfigirasyon an ajou ak siksè !");
      setPasswordInfo({ new_password: "", confirm_password: "" });
    } catch (err) {
      setError(err.error || err.detail || err.message || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (typeof URL !== "undefined" && URL.createObjectURL) {
      setLogoUrl(URL.createObjectURL(file));
    }

    const formData = new FormData();
    formData.append("logo", file);

    setSaving(true);
    setSuccess("");
    setError("");
    try {
      const updated = await client("company/", { method: "PUT", body: formData });
      if (updated && updated.logo) setLogoUrl(updated.logo);
      setSuccess("Logo mis à jour avec succès !");
    } catch (err) {
      setError(err.error || "Erreur lors de l'upload du logo");
    } finally {
      setSaving(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const ticketItems = [
    { key: "show_logo", label: "Affiche logo", desc: "Affiche logo konpayi an anlè fich la" },
    { key: "show_footer", label: "Affiche pied de fiche", desc: "Affiche mesaj ki anba fich la" },
    { key: "show_address", label: "Affiche adresse", desc: "Affiche adrès biwo oswa syèj la" },
    { key: "show_company_name", label: "Affiche nom borlette", desc: "Affiche non konpayi bank lan" },
    { key: "show_phone", label: "Affiche téléphone", desc: "Affiche nimewo telefòn sipò a" },
    { key: "show_free_mariage_price", label: "Affiche prix mariage gratuit", desc: "Affiche montan oswa 'Gratuit' pou maryaj gratis yo" },
    { key: "show_branch", label: "Affiche succursale", desc: "Affiche non sikisal kote fich la fèt la" },
    { key: "show_agent", label: "Affiche nom vendeur", desc: "Affiche non ajan ki vann fich la" },
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <div className="text-muted small mt-2">Chargement des données du compte...</div>
      </div>
    );
  }

  const isSuperAdmin = user && user.role === "SUPERADMIN";
  const canEditLogo = user && (user.role === "COMPANY_ADMIN" || isSuperAdmin);
  const canViewBilling = user && (user.role === "COMPANY_ADMIN" || isSuperAdmin);
  const billingMonthName = billingData && billingData.year && billingData.month
    ? new Date(billingData.year, billingData.month - 1).toLocaleString("fr-FR", { month: "long", year: "numeric" })
    : "";
  const roleLabel = user && user.role ? String(user.role).replace(/_/g, " ") : "";

  return (
    <div style={{ padding: "16px 0" }}>
      {success && (
        <Alert variant="success" className="py-2 px-3 small mb-3">
          <FontAwesomeIcon icon={faCheck} className="me-2" />
          {success}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" className="py-2 px-3 small mb-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
        </Alert>
      )}

      {/* En-tête compte */}
      <Card className="border-0 shadow-sm mb-4" style={cardStyle}>
        <Card.Body className="p-4">
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between" style={{ gap: "16px" }}>
            <div className="d-flex align-items-center" style={{ gap: "16px" }}>
              <div style={{ position: "relative" }}>
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={profileInfo.name || "Logo"}
                    style={{
                      width: "72px",
                      height: "72px",
                      objectFit: "contain",
                      borderRadius: "8px",
                      border: "1px solid #DDD",
                      backgroundColor: "#FFF",
                      padding: "6px",
                    }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: "72px",
                      height: "72px",
                      borderRadius: "8px",
                      backgroundColor: "#F8F9FA",
                      border: "1px solid #DDD",
                    }}
                  >
                    <FontAwesomeIcon icon={faBuilding} className="text-muted" />
                  </div>
                )}
                {canEditLogo && (
                  <button
                    type="button"
                    onClick={() => logoInputRef.current && logoInputRef.current.click()}
                    title="Changer le logo"
                    style={{
                      position: "absolute",
                      bottom: "-4px",
                      right: "-4px",
                      width: "26px",
                      height: "26px",
                      borderRadius: "50%",
                      backgroundColor: "#007BFF",
                      border: "none",
                      color: "#FFF",
                      fontSize: "11px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <FontAwesomeIcon icon={faUpload} />
                    <input
                      type="file"
                      ref={logoInputRef}
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                  </button>
                )}
              </div>
              <div>
                <h4 style={{ fontWeight: "bold", color: "#333", margin: 0 }}>
                  {profileInfo.name || "Mon compte"}
                </h4>
                <div className="text-muted small mt-1">
                  {profileInfo.owner || (user && user.username) || "Gestion du compte borlette"}
                </div>
                {roleLabel ? (
                  <Badge bg="primary" className="mt-2" style={{ fontWeight: 500, fontSize: "0.7rem" }}>
                    {roleLabel}
                  </Badge>
                ) : null}
              </div>
            </div>

            <Button
              variant="outline-secondary"
              size="sm"
              className="fw-bold"
              style={{ borderRadius: "4px" }}
              onClick={() => window.location.reload()}
            >
              <FontAwesomeIcon icon={faSync} className="me-1" /> Actualiser
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Informations */}
      <Card className="border-0 shadow-sm mb-4" style={cardStyle}>
        <Card.Header className="bg-white border-0 pt-4 pb-0">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <FontAwesomeIcon icon={faUser} className="text-muted" />
            <div>
              <h4 style={{ fontWeight: "bold", color: "#333", margin: 0 }}>Informations</h4>
              <div className="text-muted small">Profil du propriétaire et de la compagnie</div>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-bold small mb-1" style={labelStyle}>Propriétaire</Form.Label>
                <Form.Control
                  type="text"
                  value={profileInfo.owner}
                  onChange={(e) => setProfileInfo({ ...profileInfo, owner: e.target.value })}
                  placeholder="Nom du propriétaire"
                  style={inputStyle}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-bold small mb-1" style={labelStyle}>
                  Nom
                  {!isSuperAdmin && (
                    <Badge bg="light" text="dark" className="ms-2 border" style={{ fontSize: "10px", fontWeight: "normal" }}>
                      <FontAwesomeIcon icon={faLock} className="me-1" /> non modifiable
                    </Badge>
                  )}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={profileInfo.name}
                  onChange={(e) => setProfileInfo({ ...profileInfo, name: e.target.value })}
                  placeholder="Nom de la banque"
                  disabled={!isSuperAdmin}
                  style={Object.assign({}, inputStyle, !isSuperAdmin ? { backgroundColor: "#EAEAEA", color: "#666" } : {})}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-bold small mb-1" style={labelStyle}>Téléphone</Form.Label>
                <Form.Control
                  type="text"
                  value={profileInfo.phone}
                  onChange={(e) => {
                    setProfileInfo({ ...profileInfo, phone: e.target.value });
                    setConfigInfo({ ...configInfo, header_phone: e.target.value });
                  }}
                  placeholder="Numéro de téléphone"
                  style={inputStyle}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-bold small mb-1" style={labelStyle}>
                  Montant par POS
                  <Badge bg="light" text="dark" className="ms-2 border" style={{ fontSize: "10px", fontWeight: "normal" }}>
                    <FontAwesomeIcon icon={faLock} className="me-1" /> non modifiable
                  </Badge>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={profileInfo.rate_per_pos}
                  disabled
                  placeholder="10 $ USD"
                  style={Object.assign({}, inputStyle, { backgroundColor: "#EAEAEA", color: "#666" })}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Facturation du Mois en cours */}
      {canViewBilling && (
        <Card className="border-0 shadow-sm mb-4" style={cardStyle}>
          <Card.Header className="bg-white border-0 pt-4 pb-0">
            <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "10px" }}>
              <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-muted" style={{ fontSize: "1.2rem" }} />
                <div>
                  <h4 style={{ fontWeight: "bold", color: "#333", margin: 0 }}>Facturation du Mois</h4>
                  <div className="text-muted small">
                    Facture estimée pour {billingMonthName || "le mois en cours"}
                  </div>
                </div>
              </div>
              <Badge bg="primary" className="px-3 py-2 text-white text-uppercase" style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                {billingMonthName || "Mois en cours"}
              </Badge>
            </div>
          </Card.Header>
          <Card.Body>
            {billingLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" variant="primary" />
                <span className="ms-2 text-muted small">Chargement de la facture...</span>
              </div>
            ) : billingData ? (
              <>
                <Row className="g-3 mb-3">
                  <Col xs={12} sm={4}>
                    <div
                      style={{
                        padding: "16px",
                        backgroundColor: "#F8F9FA",
                        borderRadius: "8px",
                        border: "1px solid #EAEAEA",
                        borderLeft: "4px solid #007BFF",
                      }}
                    >
                      <div className="text-muted small fw-bold text-uppercase">Facture Estimée</div>
                      <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#111", marginTop: "4px" }}>
                        {(billingData.total_billing_usd || 0).toFixed(2)} $ USD
                      </div>
                      <div className="text-muted small mt-1" style={{ fontSize: "0.75rem" }}>
                        Basé sur les agents actifs
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} sm={4}>
                    <div
                      style={{
                        padding: "16px",
                        backgroundColor: "#F8F9FA",
                        borderRadius: "8px",
                        border: "1px solid #EAEAEA",
                        borderLeft: "4px solid #28A745",
                      }}
                    >
                      <div className="text-muted small fw-bold text-uppercase">Agents Facturables</div>
                      <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#28A745", marginTop: "4px" }}>
                        {billingData.active_agents_count || 0}
                      </div>
                      <div className="text-muted small mt-1" style={{ fontSize: "0.75rem" }}>
                        Ventes ≥ {(billingData.threshold_used || 7500).toLocaleString()} HTG
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} sm={4}>
                    <div
                      style={{
                        padding: "16px",
                        backgroundColor: "#F8F9FA",
                        borderRadius: "8px",
                        border: "1px solid #EAEAEA",
                        borderLeft: "4px solid #FD7E14",
                      }}
                    >
                      <div className="text-muted small fw-bold text-uppercase">Tarif par Agent</div>
                      <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#FD7E14", marginTop: "4px" }}>
                        {(billingData.rate_applied || 10).toFixed(2)} $ USD
                      </div>
                      <div className="text-muted small mt-1" style={{ fontSize: "0.75rem" }}>
                        Prix unitaire mensuel
                      </div>
                    </div>
                  </Col>
                </Row>

                {billingData.active_agents_list && billingData.active_agents_list.length > 0 ? (
                  <div className="mt-3">
                    <h6 className="fw-bold text-dark mb-2" style={{ fontSize: "0.85rem" }}>
                      Agents qualifiés ce mois-ci ({billingData.active_agents_list.length})
                    </h6>
                    <div className="table-responsive">
                      <Table bordered hover size="sm" className="mb-0" style={{ borderColor: "#EAEAEA" }}>
                        <thead>
                          <tr style={{ backgroundColor: "#F8F9FA" }}>
                            <th style={{ fontSize: "0.8rem", padding: "10px 14px", color: "#333" }}>Agent</th>
                            <th style={{ fontSize: "0.8rem", padding: "10px 14px", color: "#333", textAlign: "right" }}>Ventes du mois</th>
                            <th style={{ fontSize: "0.8rem", padding: "10px 14px", color: "#333", textAlign: "center", width: "120px" }}>Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {billingData.active_agents_list.map((ag) => (
                            <tr key={ag.id} style={{ verticalAlign: "middle" }}>
                              <td style={{ fontSize: "0.85rem", padding: "8px 14px", fontWeight: 600 }}>
                                {ag.username}
                              </td>
                              <td style={{ fontSize: "0.85rem", padding: "8px 14px", textAlign: "right", fontWeight: 600 }}>
                                {(ag.sales_total || 0).toLocaleString()} HTG
                              </td>
                              <td style={{ fontSize: "0.85rem", padding: "8px 14px", textAlign: "center" }}>
                                <Badge bg="success" style={{ fontSize: "0.7rem", fontWeight: 600 }}>
                                  Facturable
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 text-center text-muted small bg-light rounded mt-2" style={{ border: "1px dashed #DDD" }}>
                    Aucun agent n'a encore atteint le seuil de qualification de {(billingData.threshold_used || 7500).toLocaleString()} HTG pour ce mois.
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted small text-center py-3">
                Impossible de charger les données de facturation.
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Configuration ticket */}
      <Card className="border-0 shadow-sm mb-4" style={cardStyle}>
        <Card.Header className="bg-white border-0 pt-4 pb-0">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <FontAwesomeIcon icon={faBuilding} className="text-muted" />
            <div>
              <h4 style={{ fontWeight: "bold", color: "#333", margin: 0 }}>Configuration</h4>
              <div className="text-muted small">En-tête et pied de fiche imprimée</div>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-bold small mb-1" style={labelStyle}>Adresse</Form.Label>
                <Form.Control
                  type="text"
                  value={configInfo.address}
                  onChange={(e) => setConfigInfo({ ...configInfo, address: e.target.value })}
                  placeholder="Lagonave, Haïti"
                  style={inputStyle}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-bold small mb-1" style={labelStyle}>Message (pied de fiche)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={configInfo.footer_message}
                  onChange={(e) => setConfigInfo({ ...configInfo, footer_message: e.target.value })}
                  placeholder="Mesi paskew chwazi DISIP LOTTO"
                  style={Object.assign({}, inputStyle, { height: "auto" })}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Gestion de la fiche */}
      <Card className="border-0 shadow-sm mb-4" style={cardStyle}>
        <Card.Header className="bg-white border-0 pt-4 pb-0">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <FontAwesomeIcon icon={faTicketAlt} className="text-muted" />
            <div>
              <h4 style={{ fontWeight: "bold", color: "#333", margin: 0 }}>Gestion de la fiche</h4>
              <div className="text-muted small">Opsyon afichaj sur les tickets imprimés</div>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="table-responsive mb-4">
            <Table bordered hover className="align-items-center mb-0" style={{ borderCollapse: "collapse", borderColor: "#EAEAEA" }}>
              <thead>
                <tr style={{ backgroundColor: "#FAF9F9" }}>
                  <th style={{ padding: "12px 16px", fontSize: "0.85rem", color: "#111", fontWeight: "bold" }}>Option</th>
                  <th style={{ padding: "12px 16px", fontSize: "0.85rem", color: "#111", fontWeight: "bold" }}>Description</th>
                  <th style={{ padding: "12px 16px", fontSize: "0.85rem", color: "#111", fontWeight: "bold", textAlign: "center", width: "140px" }}>Statut</th>
                </tr>
              </thead>
              <tbody>
                {ticketItems.map((item) => (
                  <tr key={item.key} style={{ verticalAlign: "middle" }}>
                    <td style={{ padding: "12px 16px", fontSize: "0.9rem", color: "#333", fontWeight: 500 }}>{item.label}</td>
                    <td style={{ padding: "12px 16px", fontSize: "0.85rem", color: "#666" }}>{item.desc}</td>
                    <td style={{ padding: "12px 16px", textAlign: "center" }}>
                      <Form.Check
                        type="switch"
                        id={`table_toggle_${item.key}`}
                        checked={!!ticketOptions[item.key]}
                        onChange={() => handleToggleOption(item.key)}
                        className="d-inline-block"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <Row className="align-items-center">
            <Col xs={12} md={8}>
              <h6 className="fw-bold mb-1" style={{ color: "#111" }}>Délai d'annulation des fiches</h6>
              <p className="text-muted small mb-0">
                Temps maximum (en minutes) accordé à un vendeur pour annuler une fiche après sa création.
              </p>
            </Col>
            <Col xs={12} md={4} className="mt-2 mt-md-0">
              <Form.Control
                type="number"
                min="1"
                max="1440"
                value={ticketOptions.max_cancel_minutes}
                onChange={(e) =>
                  setTicketOptions((prev) =>
                    Object.assign({}, prev, { max_cancel_minutes: parseInt(e.target.value, 10) || 5 })
                  )
                }
                style={Object.assign({}, inputStyle, { maxWidth: "160px", marginLeft: "auto" })}
                className="text-center"
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Sécurité */}
      <Card className="border-0 shadow-sm mb-4" style={cardStyle}>
        <Card.Header className="bg-white border-0 pt-4 pb-0">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <FontAwesomeIcon icon={faShieldAlt} className="text-muted" />
            <div>
              <h4 style={{ fontWeight: "bold", color: "#333", margin: 0 }}>Sécurité</h4>
              <div className="text-muted small">Laissez vide pour ne pas changer le mot de passe</div>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-bold small mb-1" style={labelStyle}>Nouveau mot de passe</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="••••••••"
                  value={passwordInfo.new_password}
                  onChange={(e) => setPasswordInfo({ ...passwordInfo, new_password: e.target.value })}
                  style={inputStyle}
                  autoComplete="new-password"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-bold small mb-1" style={labelStyle}>Confirmer le mot de passe</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="••••••••"
                  value={passwordInfo.confirm_password}
                  onChange={(e) => setPasswordInfo({ ...passwordInfo, confirm_password: e.target.value })}
                  style={inputStyle}
                  autoComplete="new-password"
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end">
        <Button
          className="px-4 py-2 fw-bold text-white"
          style={primaryBtnStyle}
          onClick={handleSaveAll}
          disabled={saving}
        >
          {saving ? (
            <span>
              <FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Enregistrement...
            </span>
          ) : (
            <span>
              <FontAwesomeIcon icon={faCheck} className="me-2" /> Enregistrer
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}

export default Profile;
